import * as THREE from 'three';
import {
  TANK_BODY_WIDTH,
  TANK_BODY_HEIGHT,
  TANK_BODY_DEPTH,
  TANK_TURRET_RADIUS,
  TANK_TURRET_HEIGHT,
  TANK_BARREL_LENGTH,
  TANK_BARREL_RADIUS,
  TANK_Y_POS,
} from '../config';

/**
 * 坦克基类 - 构建 3D 坦克模型（车体 + 履带轮 + 炮塔 + 炮管）
 */
export class Tank {
  readonly group = new THREE.Group();
  readonly turretGroup = new THREE.Group(); // 炮塔旋转组
  readonly barrelGroup = new THREE.Group(); // 炮管后坐力动画组
  readonly bodyMesh: THREE.Mesh;
  readonly turretMesh: THREE.Mesh;
  readonly barrelMesh: THREE.Mesh;

  // 履带轮子
  readonly wheels: THREE.Mesh[] = [];
  private wheelAngle = 0;

  // 后坐力
  recoilAmount = 0;
  private recoilSpeed = 4; // 回弹速度

  hp: number;
  maxHp: number;
  moveSpeed: number;
  alive = true;

  // 碰撞参数
  readonly bodyHalfWidth = TANK_BODY_WIDTH / 2;
  readonly bodyHalfDepth = TANK_BODY_DEPTH / 2;

  constructor(
    bodyColor: string,
    turretColor: string,
    hp: number,
    speed: number,
  ) {
    this.hp = hp;
    this.maxHp = hp;
    this.moveSpeed = speed;

    // ========== 车体 ==========
    const bodyGeo = new THREE.BoxGeometry(TANK_BODY_WIDTH, TANK_BODY_HEIGHT, TANK_BODY_DEPTH);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: bodyColor,
      roughness: 0.55,
      metalness: 0.3,
    });
    this.bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
    this.bodyMesh.castShadow = true;
    this.bodyMesh.receiveShadow = true;
    this.bodyMesh.position.y = TANK_Y_POS;
    this.group.add(this.bodyMesh);

    // ========== 履带轮子 (左右各3个) ==========
    this.createWheels();

    // ========== 炮塔 ==========
    const turretGeo = new THREE.CylinderGeometry(TANK_TURRET_RADIUS, TANK_TURRET_RADIUS + 0.15, TANK_TURRET_HEIGHT, 12);
    const turretMat = new THREE.MeshStandardMaterial({
      color: turretColor,
      roughness: 0.4,
      metalness: 0.4,
    });
    this.turretMesh = new THREE.Mesh(turretGeo, turretMat);
    this.turretMesh.castShadow = true;
    this.turretMesh.position.y = TANK_BODY_HEIGHT + TANK_TURRET_HEIGHT / 2 - 0.1;
    this.turretGroup.add(this.turretMesh);

    // ========== 炮管 (放在 barrelGroup 中实现后坐力) ==========
    const barrelGeo = new THREE.CylinderGeometry(TANK_BARREL_RADIUS, TANK_BARREL_RADIUS + 0.05, TANK_BARREL_LENGTH, 8);
    const barrelMat = new THREE.MeshStandardMaterial({
      color: '#555',
      roughness: 0.35,
      metalness: 0.6,
    });
    this.barrelMesh = new THREE.Mesh(barrelGeo, barrelMat);
    this.barrelMesh.castShadow = true;
    this.barrelMesh.rotation.x = Math.PI / 2;
    this.barrelMesh.position.set(0, TANK_BODY_HEIGHT + TANK_TURRET_HEIGHT / 2 - 0.1, TANK_BARREL_LENGTH / 2);
    this.barrelGroup.add(this.barrelMesh);

    // 炮管口装饰
    const muzzleGeo = new THREE.TorusGeometry(TANK_BARREL_RADIUS + 0.06, 0.06, 8, 12);
    const muzzleMat = new THREE.MeshStandardMaterial({
      color: '#333',
      roughness: 0.3,
      metalness: 0.7,
    });
    const muzzle = new THREE.Mesh(muzzleGeo, muzzleMat);
    muzzle.position.set(0, TANK_BODY_HEIGHT + TANK_TURRET_HEIGHT / 2 - 0.1, TANK_BARREL_LENGTH);
    this.barrelGroup.add(muzzle);

    this.turretGroup.add(this.barrelGroup);
    this.turretGroup.position.y = TANK_Y_POS;
    this.group.add(this.turretGroup);
  }

  private createWheels(): void {
    const wheelRadius = 0.35;
    const wheelWidth = 0.2;
    const wheelGeo = new THREE.CylinderGeometry(wheelRadius, wheelRadius, wheelWidth, 8);
    const wheelMat = new THREE.MeshStandardMaterial({
      color: '#1a1a1a',
      roughness: 0.7,
      metalness: 0.5,
    });

    const wheelPositions = [
      -TANK_BODY_DEPTH * 0.28,  // 前轮
      0,                          // 中轮
      TANK_BODY_DEPTH * 0.28,   // 后轮
    ];

    for (const zOffset of wheelPositions) {
      // 左侧轮
      const leftWheel = new THREE.Mesh(wheelGeo, wheelMat);
      leftWheel.rotation.x = Math.PI / 2;
      leftWheel.position.set(-TANK_BODY_WIDTH / 2 - 0.1, 0.35, zOffset);
      leftWheel.castShadow = true;
      this.wheels.push(leftWheel);
      this.group.add(leftWheel);

      // 右侧轮
      const rightWheel = new THREE.Mesh(wheelGeo, wheelMat);
      rightWheel.rotation.x = Math.PI / 2;
      rightWheel.position.set(TANK_BODY_WIDTH / 2 + 0.1, 0.35, zOffset);
      rightWheel.castShadow = true;
      this.wheels.push(rightWheel);
      this.group.add(rightWheel);
    }
  }

  /** 更新履带轮旋转 (根据移动速度) */
  updateWheels(delta: number, speedRatio: number): void {
    const wheelRadius = 0.35;
    const angularSpeed = (this.moveSpeed * speedRatio) / wheelRadius;
    this.wheelAngle += angularSpeed * delta;
    for (const wheel of this.wheels) {
      wheel.rotation.z = this.wheelAngle;
    }
  }

  /** 触发开火后坐力 */
  triggerRecoil(): void {
    this.recoilAmount = -0.4; // 炮管后移
  }

  /** 更新后坐力动画 */
  updateRecoil(delta: number): void {
    if (this.recoilAmount < 0) {
      this.recoilAmount += this.recoilSpeed * delta;
      if (this.recoilAmount > 0) this.recoilAmount = 0;
    }
    this.barrelGroup.position.z = this.recoilAmount;
  }

  /** 获取炮口世界坐标 */
  getMuzzleWorldPos(target: THREE.Vector3): THREE.Vector3 {
    // 后坐力偏移也要考虑进去
    const local = new THREE.Vector3(0, TANK_BODY_HEIGHT + TANK_TURRET_HEIGHT / 2 - 0.1, TANK_BARREL_LENGTH + this.recoilAmount);
    this.turretGroup.localToWorld(local);
    return target.copy(local);
  }

  /** 获取炮管前方向量(世界坐标) */
  getBarrelDirection(target: THREE.Vector3): THREE.Vector3 {
    target.set(0, 0, 1);
    const turretQuat = new THREE.Quaternion();
    this.turretGroup.getWorldQuaternion(turretQuat);
    target.applyQuaternion(turretQuat);
    target.normalize();
    return target;
  }

  dispose(): void {
    this.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    });
  }
}
